import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit as qlimit,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { CATEGORIES, type Category } from '@sfp/shared/roles';
import { categoryLabel } from '../../lib/format.js';

interface DocRow {
  id: string;
  originalName?: string;
  category?: string;
  docType?: string;
  filingEntity?: string;
  status?: string;
  llm?: { confidence?: number };
}

/**
 * Reclassification app. Admin-only. Lets you correct a document's category,
 * docType, and filing entity inline, or re-run the AI classifier on it. Filter
 * by entity/category/status to find docs that need fixing.
 */
export function Reclassify() {
  const { role } = useAuth();
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [entities, setEntities] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    const u1 = onSnapshot(
      query(collection(db, 'documents'), orderBy('uploadedAt', 'desc'), qlimit(500)),
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DocRow, 'id'>) }))),
      () => setDocs([]),
    );
    const u2 = onSnapshot(query(collection(db, 'entities'), qlimit(300)), (snap) => {
      setEntities(
        snap.docs
          .map((d) => ((d.data() as { legalName?: string }).legalName ?? '').trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b)),
      );
    });
    return () => {
      u1();
      u2();
    };
  }, []);

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (catFilter !== 'all' && d.category !== catFilter) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (s && !(d.originalName ?? '').toLowerCase().includes(s) && !(d.filingEntity ?? '').toLowerCase().includes(s))
        return false;
      return true;
    });
  }, [docs, search, catFilter, statusFilter]);

  if (role !== 'admin') {
    return <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">Admins only.</div>;
  }

  async function patch(id: string, fields: Partial<DocRow>) {
    setSavingId(id);
    setSavedId(null);
    try {
      await updateDoc(doc(db, 'documents', id), {
        ...fields,
        reclassifiedAt: serverTimestamp(),
        reclassifiedManually: true,
      });
      setSavedId(id);
      setTimeout(() => setSavedId((cur) => (cur === id ? null : cur)), 1500);
    } finally {
      setSavingId(null);
    }
  }

  async function reRunAi(id: string) {
    // Re-fire the classifier by flipping status back to 'classifying'.
    setSavingId(id);
    try {
      await updateDoc(doc(db, 'documents', id), { status: 'classifying' });
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">Documents</div>
          <h1 className="text-2xl font-semibold text-stone-800">Reclassify</h1>
          <p className="text-sm text-stone-500">
            Correct a document's category, type, or entity inline — or re-run the AI classifier. Changes
            flow straight to the <Link to="/documents/library" className="text-[color:var(--sfp-primary)] hover:underline">Library</Link> and Entity Documents.
          </p>
        </div>
        <Link to="/documents" className="text-xs text-stone-500 hover:text-stone-800">
          ← All documents
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name or entity…"
          className="h-8 flex-1 min-w-[200px] rounded border border-stone-300 px-3 text-xs"
        />
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="h-8 rounded border border-stone-300 px-2 text-xs">
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{categoryLabel(c)}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-8 rounded border border-stone-300 px-2 text-xs">
          <option value="all">All statuses</option>
          {['ready', 'triage', 'error', 'classifying', 'uploaded'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="text-[11px] text-stone-400">{rows.length} doc(s)</span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <table className="w-full text-xs">
          <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-3 py-2">Document</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Doc type</th>
              <th className="px-3 py-2">Filing entity</th>
              <th className="px-3 py-2 text-right">AI</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-stone-400">No documents match.</td></tr>
            ) : rows.map((d) => (
              <tr key={d.id} className="border-b border-stone-100 align-top last:border-b-0">
                <td className="px-3 py-2">
                  <Link to={`/documents/${d.id}`} className="font-medium text-stone-800 hover:underline">
                    {d.originalName ?? d.id}
                  </Link>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-stone-400">
                    <StatusDot status={d.status} />
                    {d.status}
                    {savedId === d.id && <span className="text-emerald-600">saved ✓</span>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={d.category ?? 'other'}
                    disabled={savingId === d.id}
                    onChange={(e) => patch(d.id, { category: e.target.value as Category })}
                    className="w-full rounded border border-stone-300 px-1.5 py-1"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{categoryLabel(c)}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    defaultValue={d.docType ?? ''}
                    disabled={savingId === d.id}
                    onBlur={(e) => {
                      if (e.target.value !== (d.docType ?? '')) patch(d.id, { docType: e.target.value });
                    }}
                    placeholder="doc type"
                    className="w-full rounded border border-stone-300 px-1.5 py-1"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    list="reclass-entities"
                    defaultValue={d.filingEntity ?? ''}
                    disabled={savingId === d.id}
                    onBlur={(e) => {
                      if (e.target.value !== (d.filingEntity ?? '')) patch(d.id, { filingEntity: e.target.value.trim() });
                    }}
                    placeholder="entity"
                    className="w-full rounded border border-stone-300 px-1.5 py-1"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    disabled={savingId === d.id}
                    onClick={() => reRunAi(d.id)}
                    className="rounded border border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                    title="Re-run the AI classifier on this document"
                  >
                    Re-run
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <datalist id="reclass-entities">
        {entities.map((e) => <option key={e} value={e} />)}
      </datalist>
    </div>
  );
}

function StatusDot({ status }: { status?: string }) {
  const c =
    status === 'ready' ? 'bg-emerald-500'
    : status === 'error' ? 'bg-[#9C4A3A]'
    : status === 'triage' ? 'bg-orange-400'
    : status === 'classifying' ? 'bg-amber-400'
    : 'bg-stone-300';
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${c}`} />;
}
